import { FC } from 'react'
import { BodySmall, Title6 } from './styles/Typography'
import { Grid } from './Grid'
import { SuperImage } from './SuperImage'
import { Link } from 'react-router-dom'
import styled from 'styled-components'
import { theme } from '../theme/theme'
import { Accordion } from './Accordion'
import { IFaqSectionFields, IFaqItem } from '@estercare/ester-shared'
import { documentToReactComponents } from '@contentful/rich-text-react-renderer'
import { getOptions } from './richText/options'
import { useWindowWidth } from '../hooks/useWindowWidth'
import { isDesktop } from '../utils/devices'
import { Arrow } from './icons'
import { useTranslation } from 'react-i18next'
import { useFilteredContent } from '../hooks/useFilteredContent'

export const FAQItem: FC<{ faq: IFaqItem }> = ({ faq }) => {
  const { question, answer } = faq.fields
  if (!question || !answer) return null
  return <Accordion title={question}>{answer && documentToReactComponents(answer, getOptions(true))}</Accordion>
}

export const FAQ: FC<IFaqSectionFields & { showSupportLink?: boolean; showImage?: boolean }> = ({
  faqItems,
  imageAlignment,
  image,
  showSupportLink = false,
  backgroundColor,
  showImage = true,
}) => {
  const { t } = useTranslation()
  const width = useWindowWidth()
  const filteredFaqItems = useFilteredContent(faqItems)

  return (
    <StyledGrid $columns={isDesktop(width) && showImage ? 2 : 1} $backgroundColor={backgroundColor}>
      <Container $imageAlignment={imageAlignment}>
        {showImage && <SuperImage asset={image} width={300} height={400} />}
      </Container>
      <Container>
        <StyledTitle6>{t('common.faq.title')}</StyledTitle6>

        {filteredFaqItems.map((faq, index) => (
          <FAQItem faq={faq} key={index} />
        ))}
        {showSupportLink && (
          <StyledLinkBodySmall>
            {t('common.faq.noAnswerText')}{' '}
            <Link to="/portal/support">
              <StyledLinkSpan>
                {t('common.faq.visitSupport')} <Arrow size={16} />
              </StyledLinkSpan>
            </Link>
          </StyledLinkBodySmall>
        )}
      </Container>
    </StyledGrid>
  )
}
type BackgroundColor = 'beigeDark' | 'beigeLight'

const StyledGrid = styled(Grid)<{ $backgroundColor?: BackgroundColor }>`
  background-color: ${({ $backgroundColor }) =>
    ($backgroundColor && theme.color[$backgroundColor]) || theme.color.beigeLight};
`

const Container = styled.div<{ $imageAlignment?: boolean }>`
  order: ${({ $imageAlignment }) => ($imageAlignment ? 1 : 0)};
  justify-self: ${({ $imageAlignment }) => ($imageAlignment ? 'center' : 'start')};
  align-self: center;

  @media screen and (max-width: ${theme.breakpoint.large}px) {
    &:first-child {
      display: none;
    }
  }
`

const StyledTitle6 = styled(Title6)`
  margin-bottom: ${theme.spacing.medium}px;
`

const StyledLinkBodySmall = styled(BodySmall)`
  && {
    font-weight: 375;
    font-size: 14.8px;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: ${theme.spacing.tiny}px;
  }
`
const StyledLinkSpan = styled.span`
  color: ${theme.color.plumMid};
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing.tiny}px;
  font-weight: 500;
`
